import { useAsyncEffect } from '@/hooks';
import { Message, MessageHeader, Segment, Tab, TabPane, TabProps } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import Breadcrumbs from '../Breadcrumbs';
import ButtonBar from '../ButtonBar';
import { OnboardingSubmissionDetailsAddressContact } from './OnboardingSubmissionDetailsAddressContact';
import { OnboardingSubmissionDetailsApproveConfirmationModal } from './OnboardingSubmissionDetailsApproveConfirmationModal';
import { OnboardingSubmissionDetailsBankingInfo } from './OnboardingSubmissionDetailsBankingInfo';
import { OnboardingSubmissionDetailsDocuments } from './OnboardingSubmissionDetailsDocuments';
import { OnboardingSubmissionDetailsEmploymentInfo } from './OnboardingSubmissionDetailsEmploymentInfo';
import { OnboardingSubmissionDetailsForms } from './OnboardingSubmissionDetailsForms';
import { OnboardingSubmissionDetailsPayrollInfo } from './OnboardingSubmissionDetailsPayrollInfo';
import { OnboardingSubmissionDetailsPersonal } from './OnboardingSubmissionDetailsPersonal';
import { OnboardingSubmissionDetailsRejectConfirmationModal } from './OnboardingSubmissionDetailsRejectConfirmationModal';
import { OnboardingSubmissionDetailsSyncConfirmationModal } from './OnboardingSubmissionDetailsSyncConfirmationModal';

enum TabsItem {
  Personal = 'Personal',
  AddressContact = 'Address & Contact',
  PayrollInfo = 'Payroll Info',
  EmploymentInfo = 'Employment Info',
  BankingInfo = 'Banking Info',
  Documents = 'Documents',
  Forms = 'Forms',
}

export const OnboardingSubmissionDetails = observer(() => {
  const { temporaryEmployeeId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { temporaryEmployeeStore, userStore } = useStores();
  const fullName = temporaryEmployeeStore.current?.user?.fullName;
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const selectedTabIndex = Object.values(TabsItem).indexOf(selectedTab);
  const [shouldShowApproveConfirmationModal, setShouldShowApproveConfirmationModal] =
    useState(false);
  const [shouldShowRejectionConfirmationModal, setShouldShowRejectionConfirmationModal] =
    useState(false);
  const [shouldShowSyncConfirmationModal, setShouldShowSyncConfirmationModal] = useState(false);

  useEffect(() => {
    const foundTab = Object.entries(tabPaths).find(([, path]) => pathname.includes(path));

    if (foundTab) {
      setSelectedTab(foundTab[0] as TabsItem);
    } else {
      setSelectedTab(defaultTab);
    }
  }, [pathname]);

  useAsyncEffect(async () => {
    if (temporaryEmployeeId) {
      await temporaryEmployeeStore.fetchById(temporaryEmployeeId);
    }
  }, [temporaryEmployeeId]);

  if (temporaryEmployeeStore.current?.status === 'pending') {
    return (
      <Segment basic padded="very">
        <Message negative>
          <MessageHeader>You cannot view this page</MessageHeader>
          <p>The employee hasn&apos;t submitted their onboarding information yet.</p>
        </Message>
      </Segment>
    );
  }

  return (
    <>
      <Segment basic padded="very">
        <Breadcrumbs
          title="Onboarding Submissions"
          link={`/onboarding/submissions/${temporaryEmployeeStore.current?.status}`}
          linkTitle="Onboarding Submissions"
          activeBreadcrumb={fullName ? `${fullName}'s Onboarding Submission` : ''}
        />

        <Tab
          activeIndex={selectedTabIndex}
          className="vertical_tab"
          menu={{ fluid: true, vertical: true, tabular: true }}
          panes={tabPanes}
          onTabChange={(_, { activeIndex }) => {
            const newIndex = typeof activeIndex === 'string' ? Number(activeIndex) : activeIndex;

            if (newIndex !== undefined) {
              const newSelectedTab = Object.values(TabsItem)[newIndex];
              const tabPath = tabPaths[newSelectedTab];

              setSelectedTab(newSelectedTab);
              navigate(`/onboarding/submissions/details/${temporaryEmployeeId}/${tabPath}`);
            }
          }}
        />
      </Segment>

      {temporaryEmployeeStore.current?.status === 'submitted' && (
        <ButtonBar
          saveText="Approve"
          saveButtonColor="#058c0b"
          cancelText="Reject"
          cancelButtonColor="#ff3b30"
          disabled={false}
          onSave={() => {
            setShouldShowApproveConfirmationModal(true);
          }}
          onCancel={() => {
            setShouldShowRejectionConfirmationModal(true);
          }}
        />
      )}

      {temporaryEmployeeStore.current?.status === 'approved' && (
        <>
          <ButtonBar
            saveText="Sync to Quickbooks"
            saveButtonColor="#2b5797"
            disabled={false}
            cancelText="Reject"
            cancelButtonColor="#ff3b30"
            onCancel={() => {
              setShouldShowRejectionConfirmationModal(true);
            }}
            onSave={() => {
              setShouldShowSyncConfirmationModal(true);
            }}
          />
        </>
      )}

      <OnboardingSubmissionDetailsRejectConfirmationModal
        open={shouldShowRejectionConfirmationModal}
        onClose={() => setShouldShowRejectionConfirmationModal(false)}
      />

      <OnboardingSubmissionDetailsApproveConfirmationModal
        open={shouldShowApproveConfirmationModal}
        onClose={() => setShouldShowApproveConfirmationModal(false)}
      />

      <OnboardingSubmissionDetailsSyncConfirmationModal
        open={shouldShowSyncConfirmationModal}
        onClose={() => setShouldShowSyncConfirmationModal(false)}
      />
    </>
  );
});

const tabPaths = {
  [TabsItem.Personal]: 'personal',
  [TabsItem.AddressContact]: 'address-contact',
  [TabsItem.PayrollInfo]: 'payroll-info',
  [TabsItem.EmploymentInfo]: 'employment-info',
  [TabsItem.BankingInfo]: 'banking-info',
  [TabsItem.Documents]: 'documents',
  [TabsItem.Forms]: 'forms',
};
const defaultTab = TabsItem.Personal;
const tabPanes: TabProps['panes'] = [
  {
    menuItem: TabsItem.Personal,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsPersonal />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.AddressContact,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsAddressContact />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.PayrollInfo,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsPayrollInfo />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.EmploymentInfo,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsEmploymentInfo />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.BankingInfo,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsBankingInfo />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.Documents,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsDocuments />
      </TabPane>
    ),
  },
  {
    menuItem: TabsItem.Forms,
    render: () => (
      <TabPane>
        <OnboardingSubmissionDetailsForms />
      </TabPane>
    ),
  },
];
